import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import {
  ContinueButton,
  Footer,
  PreviousButton,
  QuestionFormBox,
  QuestionName,
} from "../../styles";
import {
  StyledOutlinedTextField
} from '../../../../Infrastructure/Theme/styledComponents';
import { useDispatch, useSelector } from "react-redux";
import {
  decrementSteps,
  incrementSteps,
  resetExperienceStepValues,
} from "../../../../Infrastructure/States/educatorStepsSlice";
import { useFormik } from "formik";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../Infrastructure/config";
import { ShowErrorToast } from "../../../Common/Toast/toast";
import { useNavigate } from "react-router-dom";
import { DarkBgLoader, Loader } from "../../../Common/Loader/loader";
import { EduAccFlowHeader } from "../../../Common/EduAccFlowHeader/eduAccFlowHeader";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { YesNoToggle } from "../../../Common/YesNoToggle/yesNoToggle";
import paths from "../../../../Infrastructure/Router/paths";
import ScrollToTop from "../../../../Infrastructure/Helper/scrollToTop";
import { reachStepSX } from "./styles";

export const ReachStep = ({ lastPageRoute, urlInfoObj }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const lastSubmitCount = useRef(0)
  const { title, subtitle, fromJob } = urlInfoObj
  const [submitCount, setSubmitCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const page = useSelector((state)=> state.route.currentRoute)
  const steps = useSelector((state) => state.educatorSteps.steps);
  const userData = useSelector((state) => state.userData.data);
  const socials = userData?.educator?.questions?.socials
  const platforms = userData?.educator?.questions?.platforms
  const platform1Ref = useRef(null);
  const platform2Ref = useRef(null);
  const platform3Ref = useRef(null);
  const social1Ref = useRef(null);
  const social2Ref = useRef(null);
  const social3Ref = useRef(null)
  const refs = useMemo(
    () => ({
      platformLink1: platform1Ref,
      platformLink2: platform2Ref,
      platformLink3: platform3Ref,
      socialLink1: social1Ref,
      socialLink2: social2Ref,
      socialLink3: social3Ref,
    }),
    []
  )
  const social1 = socials?.social_link_1;
  const social2 = socials?.social_link_2;
  const social3 = socials?.social_link_3;
  const platform1 = platforms?.platform_link_1;
  const platform2 = platforms?.platform_link_2;
  const platform3 = platforms?.platform_link_3;
  const ip = useSelector((state) => state.ip.address)

  const [showPlatformLinks, setShowPlatformLinks] = useState(!!(platform1 || platform2 || platform3));
  const [showSocialLinks, setShowSocialLinks] = useState(!!(social1 || social2 || social3));

  const handleDec = async () => {
    if (steps > 1) {
      try {
        setIsLoading(true)
        const updateReachStep = httpsCallable(functions, "updatereachstep");
        await updateReachStep({...formik.values, onboarding_step: 2, ip, page});
        dispatch(decrementSteps())
      } catch (error) {
        ShowErrorToast(error);
        ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
      } finally {
        setIsLoading(false)
      }
    }
  };
  const initialValues = {
    platformLink1: platform1 || "",
    platformLink2: platform2 || "",
    platformLink3: platform3 || "",
    socialLink1: social1 || "",
    socialLink2: social2 || "",
    socialLink3: social3 || ""
  };
  // Define the isUrlValid function
  function isUrlValid(userInput) {
    if (userInput) {
      const res = userInput.match(/(https?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/g);
      return res !== null;
    } return true;
  }
  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      platformLink1: Yup.string().test('platformLink1', 'Invalid link URL', (value) => isUrlValid(value)),
      platformLink2: Yup.string().test('platformLink2', 'Invalid link URL', (value) => isUrlValid(value)),
      platformLink3: Yup.string().test('platformLink3', 'Invalid link URL', (value) => isUrlValid(value)),
      socialLink1: Yup.string().test('socialLink1', 'Invalid link URL', (value) => isUrlValid(value)),
      socialLink2: Yup.string().test('socialLink2', 'Invalid link URL', (value) => isUrlValid(value)),
      socialLink3: Yup.string().test('socialLink3', 'Invalid link URL', (value) => isUrlValid(value)),
    }),
    onSubmit: async (values) => {
      if (steps >= 1 && steps < 4) {
        try {
          setIsLoading(true)
          const updateReachStep = httpsCallable(functions, "updatereachstep");
          await updateReachStep({...values, ip, page});
          dispatch(incrementSteps())
        } catch (error) {
          ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
        } finally {
          setIsLoading(false)
        }
      }
    },
  });
  const handleExit = async () => {
    try {
      setExitLoading(true)
      const updateEducatorStep = httpsCallable(
        functions,
        "updatereachstep"
      );
      await updateEducatorStep({ ...formik.values, onboarding_step: 3, ip, page });
      dispatch(resetExperienceStepValues());
      navigate(lastPageRoute || paths.home.path)
    } catch (err) {
      navigate(lastPageRoute || paths.home.path)
      ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
    } finally {
      setExitLoading(false)
    }
  };
  useEffect(() => {
    if (!isXs) return
    if (submitCount === lastSubmitCount.current) return;

    lastSubmitCount.current = submitCount
    const errorKeys = Object.keys(formik.errors);
    if (errorKeys.length === 0) return;

    for (const key of errorKeys) {
      const ref = refs[key];
      if (ref && ref.current && ref.current.offsetParent !== null) {
        requestAnimationFrame(() => {
          ref.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
        break
      }
    }
  }, [submitCount, formik.errors, isXs, refs]);
  return (
    <>
      <ScrollToTop />
      {exitLoading ? <DarkBgLoader height={"100vh"} /> : null}
      <EduAccFlowHeader handleExit={handleExit} steps={steps} />
      <Box
        height="auto">
        <form onSubmit={formik.handleSubmit}>
          {isLoading ? <Loader height={"100vh"} /> :
            <Grid
              container
              sx={reachStepSX.gridContainer}
            >
              <Grid lg={6} md={6} sm={10} xs={12}>
                <Box sx={reachStepSX.boxPadding}>
                  <Box sx={reachStepSX.questionBoxHeight}>
                    <QuestionName variant="h6">
                      Have you taught on any teaching platforms like Udemy, Skillshare,
                      Wyzant, etc? Share your profile link.
                    </QuestionName>
                  </Box>
                  <Box sx={reachStepSX.yesNoBox}>
                    <YesNoToggle
                      value={showPlatformLinks}
                      onChange={(newState) => {
                        setShowPlatformLinks(newState)
                        if (!newState) {
                          formik.setFieldValue('platformLink1', '')
                          formik.setFieldValue('platformLink2', '')
                          formik.setFieldValue('platformLink3', '')
                        }
                      }}
                    />
                  </Box>
                  {showPlatformLinks && (
                    <QuestionFormBox>
                      {[1, 2, 3].map((num) => (
                        <StyledOutlinedTextField
                          key={`platformLink${num}`}
                          name={`platformLink${num}`}
                          inputRef={refs[`platformLink${num}`]}
                          label={
                            formik.touched[`platformLink${num}`] && Boolean(formik.errors[`platformLink${num}`])
                              ? formik.errors[`platformLink${num}`]
                              : `Link ${num}`
                          }
                          error={formik.touched[`platformLink${num}`] && Boolean(formik.errors[`platformLink${num}`])}
                          variant="outlined"
                          placeholder="Paste your link"
                          onChange={formik.handleChange}
                          value={formik.values[`platformLink${num}`]}
                          sx={reachStepSX.textFieldMargin}
                          fullWidth
                          disabled={exitLoading}
                        />
                      ))}
                    </QuestionFormBox>
                  )}
                </Box>
              </Grid>
              <Grid lg={6} md={6} sm={10} xs={12}>
                <Box sx={reachStepSX.boxPadding}>
                  <Box sx={reachStepSX.questionBoxHeight}>
                    <QuestionName variant="h6">
                      Do you have any social media where you post educational content like Youtube,
                      Tik Tok, Twitter, etc? Share your profile link.
                    </QuestionName>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2} pl={{xs: 0, sm: 2}} mb={{xs:1, sm:0}} mt={2}>
                    <YesNoToggle
                        value={showSocialLinks}
                        onChange={(newState) => {
                          setShowSocialLinks(newState)
                          if (!newState) {
                            formik.setFieldValue('socialLink1', '')
                            formik.setFieldValue('socialLink2', '')
                            formik.setFieldValue('socialLink3', '')
                          }
                        }}
                      />
                  </Box>
                  {showSocialLinks && (
                    <QuestionFormBox>
                      {[1, 2, 3].map((num) => (
                        <StyledOutlinedTextField
                          key={`socialLink${num}`}
                          name={`socialLink${num}`}
                          inputRef={refs[`socialLink${num}`]} 
                          label={
                            formik.touched[`socialLink${num}`] && Boolean(formik.errors[`socialLink${num}`])
                              ? formik.errors[`socialLink${num}`]
                              : `Link ${num}`
                          }
                          error={formik.touched[`socialLink${num}`] && Boolean(formik.errors[`socialLink${num}`])}
                          variant="outlined"
                          placeholder="Paste your link"
                          onChange={formik.handleChange}
                          value={formik.values[`socialLink${num}`]}
                          sx={reachStepSX.textFieldMargin}
                          fullWidth
                          disabled={exitLoading}
                        />
                      ))}
                    </QuestionFormBox>
                  )}
                </Box>
              </Grid>
            </Grid>
          }
          <Footer>
            {/* Title ABOVE buttons on small screens */}
            {fromJob && (
              <Typography
                color="initial"
                sx={reachStepSX.footerTitle}
              >
                {title} - {subtitle}
              </Typography>
            )}

            <Grid container justifyContent="space-between" alignItems="center" p={2}>

              {/* Previous Button */}
              <Grid xs={1}>
                {steps > 1 && (
                  <PreviousButton
                    variant="contained"
                    onClick={handleDec}
                    disabled={exitLoading || isLoading}
                  >
                    Previous
                  </PreviousButton>
                )}
              </Grid>

              {/* Title CENTERED between buttons on large screens */}
              {fromJob && (
                <Grid
                  xs={12}
                  md="auto"
                  display={{ xs: "none", md: "flex" }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography fontWeight={600} color="initial" textAlign={'center'}>
                    {title} - {subtitle}
                  </Typography>
                </Grid>
              )}

              {/* Continue Button */}
              <Grid display="flex" justifyContent="flex-end">
                <ContinueButton
                  variant="contained"
                  type="submit"
                  onClick={()=>setSubmitCount(prev => prev + 1)}
                  disabled={exitLoading || isLoading}
                >
                  Continue
                </ContinueButton>
              </Grid>
            </Grid>
          </Footer>
        </form>
      </Box>
    </>
  );
};